import { useEffect } from 'react';
import { COMPANY } from '../data/company';

// Lightweight, dependency-free SEO head manager for the SPA.
// Sets title, meta tags, Open Graph, Twitter cards, canonical, and JSON-LD schema.

const upsertMeta = (attr, key, content) => {
  if (!content) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
};

const injectJsonLd = (id, data) => {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
};

export const SEO = ({
  title,
  description,
  keywords = [],
  canonicalPath = '/',
  ogImage,
  faqs = [],
  breadcrumbs = [],
}) => {
  useEffect(() => {
    const base = (typeof window !== 'undefined' && window.location.origin) || COMPANY.siteUrl;
    const url = `${base}${canonicalPath}`;
    const kw = Array.isArray(keywords) ? keywords.join(', ') : keywords;
    const image = ogImage || `${base}/og-image.jpg`;

    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', kw);
    upsertLink('canonical', url);

    // Open Graph
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:site_name', COMPANY.name);
    upsertMeta('property', 'og:image', image);

    // Twitter
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);

    // LocalBusiness schema
    injectJsonLd('ld-localbusiness', {
      '@context': 'https://schema.org',
      '@type': 'MovingCompany',
      name: COMPANY.name,
      description,
      url: base,
      telephone: COMPANY.phoneDisplay,
      email: COMPANY.email,
      areaServed: [...COMPANY.primaryAreas, ...COMPANY.secondaryAreas],
      address: {
        '@type': 'PostalAddress',
        addressRegion: 'FL',
        addressCountry: 'US',
      },
      priceRange: '$$',
    });

    // FAQ schema
    if (faqs && faqs.length) {
      injectJsonLd('ld-faq', {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      });
    } else {
      const old = document.getElementById('ld-faq');
      if (old) old.remove();
    }

    // Breadcrumb schema
    if (breadcrumbs && breadcrumbs.length) {
      injectJsonLd('ld-breadcrumb', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: `${base}${b.path}`,
        })),
      });
    } else {
      const old = document.getElementById('ld-breadcrumb');
      if (old) old.remove();
    }
  }, [title, description, keywords, canonicalPath, ogImage, faqs, breadcrumbs]);

  return null;
};
